import styled, { css } from 'styled-components';

import { device, hexToRGB, typography } from '@/styles/helpers';

export const ScAccountBlock = styled.div`
  position: relative;

  @media (${device.mobile}) {
    flex: auto;
  }
`;

interface ScAccountButtonProps {
  active: boolean;
}

export const ScAccountButton = styled.div<ScAccountButtonProps>`
  position: relative;
  width: 74px;
  height: 48px;
  border-radius: 40px;
  padding: 4px;
  background-color: ${({ theme }) => theme.grayLightColor};
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: flex-start;
  align-items: center;
  cursor: pointer;
  z-index: 14;
  transition:
    background-color 0.2s,
    border-color 0.2s;
  gap: 8px;

  &:hover {
    & > .arrow i {
      color: ${({ theme }) => theme.whiteColor};
    }
  }

  @media (${device.mobile}) {
    width: 100%;
  }

  & > .arrow {
    transition: transform 0.2s;
    transform: ${({ active }) => (active ? 'rotate(180deg)' : 'rotate(0deg)')};

    i {
      display: block;
      transform: rotate(90deg);

      color: ${({ theme, active }) =>
        active ? theme.whiteColor : theme.blackLightColor};
    }
  }
`;

interface ScAccountAvatarProps {
  isMobile?: boolean;
}

export const ScAccountAvatar = styled.div<ScAccountAvatarProps>`
  ${({ isMobile }) =>
    isMobile
      ? css`
          width: 56px;
          height: 56px;
        `
      : css`
          width: 40px;
          height: 40px;
        `}
`;

export const ScAccountDropDown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  background-color: ${({ theme }) => theme.grayLightColor};
  padding: 16px;
  border-radius: 19px;
  z-index: 16;
  box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.9);
  max-width: 320px;

  display: flex;
  flex-direction: column;
  gap: 16px;

  .divider {
    width: 100%;
    height: 1px;
    background-color: ${({ theme }) => hexToRGB(theme.whiteColor, 0.2)};
  }

  & > ul {
    display: block;
    list-style: none;

    & > li {
      padding: 8px 0;
      display: flex;

      &:first-child {
        padding-top: 4px;
      }
      &:last-child {
        padding-bottom: 4px;
      }

      & > a,
      & > button {
        display: block;
        width: 100%;
        text-align: left;
        font-size: 16px;
        line-height: 24px;
        color: ${({ theme }) => theme.blackLightColor};
        background: none;
        border: none;
        transition: color 0.2s;
        cursor: pointer;
        white-space: nowrap;

        &:hover {
          color: ${({ theme }) => theme.whiteColor};

          @media (${device.mobile}) {
            color: ${({ theme }) => theme.grayColor};
          }
        }

        @media (${device.mobile}) {
          color: ${({ theme }) => theme.whiteColor};
        }
      }
    }
  }
`;

export const ScAccountDropDownLinkValue = styled.div<{
  showNotification?: boolean;
}>`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  ${({ showNotification }) =>
    showNotification &&
    css`
      &::after {
        content: "";
        position: absolute;
        right: -16px;
        top: 50%;
        transform: translateY(-50%);
        height: 8px;
        width: 8px;
        background: ${({ theme }) => theme.redColor};
        border-radius: 50%;
      }
  `}
`;

interface ScSelectDepositProps {
  active: boolean;
}

export const ScSelectDeposit = styled.div<ScSelectDepositProps>`
  position: relative;

  & > .select-button {
    position: relative;
    z-index: 12;
    background-color: ${({ theme }) => theme.grayLightColor};
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: flex-start;
    align-items: center;
    height: 40px;
    min-width: 128px;
    border-width: 1px;
    border-style: solid;
    border-color: ${({ active, theme }) =>
      active ? theme.whiteColor : 'transparent'};
    border-radius: 28px;
    font-size: 16px;
    line-height: 24px;
    color: ${({ theme }) => theme.blackColor};
    transition: border-color 0.2s;
    cursor: pointer;
    padding-right: 20px;

    :hover {
      border: 1px solid ${({ theme }) => theme.whiteColor};
      & > .arrow {
        i {
          color: ${({ theme }) => theme.whiteColor};
        }
      }
    }

    & > .value {
      font-size: 16px;
      line-height: 24px;
      padding: 0 16px;
      color: ${({ theme }) => theme.whiteColor};
      white-space: nowrap;

      span {
        color: ${({ theme }) => theme.grayColor};
      }
    }

    & > .arrow {
      position: absolute;
      right: 18px;
      top: 16px;
      transition: transform 0.2s;
      transform: ${({ active }) =>
        active ? 'rotate(0deg)' : 'rotate(180deg)'};

      i {
        display: block;

        path {
          stroke: ${({ theme, active }) =>
            active ? theme.whiteColor : theme.grayColor};
        }
      }
    }
  }

  & > .options {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    z-index: 11;
    background-color: ${({ theme }) => theme.grayLightColor};
    padding-top: 48px;
    padding-bottom: 8px;
    border-radius: 19px;
    overflow: hidden;

    & > ul {
      display: block;
      max-height: 180px;
      overflow: auto;
      list-style: none;

      & > li {
        font-size: 13px;
        padding: 8px 16px;
        cursor: pointer;
        background-color: transparent;
        color: ${({ theme }) => theme.whiteColor};
        transition: color 0.2s;
        white-space: nowrap;

        span {
          color: ${({ theme }) => theme.grayColor};
          transition: 0.2s;

          &:hover {
            color: ${({ theme }) => theme.whiteColor};
          }

          @media (${device.mobile}) {
            color: ${({ theme }) => theme.whiteColor};
          }
        }
      }
    }
  }
`;

export const ScAccountInfo = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  width: 100%;
  min-height: 100%;

  .user-info {
    width: 100%;
    display: flex;
    gap: 16px;
    align-items: center;
  }
`;

export interface ScDepositPanelProps {
  mobile?: boolean;
  blackBg?: boolean;
  noPadding?: boolean;
  reducedSizeOnSmallScreen?: boolean;
}

export const ScDepositPanel = styled.div<ScDepositPanelProps>`
  display: flex;
  flex-wrap: nowrap;
  justify-content: flex-start;
  align-items: center;
  background-color: ${({ theme, blackBg }) =>
    blackBg ? theme.blackColor : theme.grayDarkColor};
  border-radius: 24px;
  padding: ${({ noPadding }) => (noPadding ? 0 : '4px 4px 4px 0')};
  flex: auto;
  width: 100%;
  max-width: max-content;
  gap: 8px;

  .cashier-button {
    button {
      border-radius: 40px;
      min-width: 78px;
      padding: 0 6px 1px 6px;
    }
  }

  ${({ mobile, reducedSizeOnSmallScreen }) =>
    mobile &&
    css`
      max-width: 100%;
      justify-content: space-between;
      gap: 8px;

      .cashier-button {
        button {
          height: 40px;
          min-width: 108px;
          padding: 0 12px;
        }
      }

      ${
        reducedSizeOnSmallScreen &&
        css`
        @media (max-width: 360px) {
          gap: 0;

          & > .cashier-button button {
            border-radius: 40px;
            min-width: 78px;
            padding: 0 3px 1px;
            font-size: 13px;
          }
        }
      `
      }
    `}
`;

export const ScUserWelcome = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-width: 100%;
  @media (${device.mobile}) {
    max-width: calc(100% - 72px);
  }

  & > .welcome {
    display: flex;
    align-items: center;
    gap: 8px;
    text-transform: uppercase;

    ${typography['body2bold']}
    line-height: 18px;

    .vip-label {
      border-radius: 360px;
      padding-inline: 8px;
      color: ${({ theme }) => theme.blackColor};
      ${typography['body2bold']}
      @media (${device.mobile}) {
        padding-block: 2px;
      }

      &.VIP1 {
        background-color: ${({ theme }) => theme.yellowColor};
      }
      &.VIP2 {
        background-color: ${({ theme }) => theme.grayMegaLightColor};
      }
      &.VIP3 {
        background-color: ${({ theme }) => theme.lightVioletColor};
      }
      &.SUPER {
        color: ${({ theme }) => theme.whiteColor};
        background: ${({ theme }) =>
          `linear-gradient(90deg, ${theme.vipSuper1} 0%, ${theme.vipSuper2} 50%, ${theme.vipSuper3} 100%)`};
      }
    }
  }

  & > .email {
    ${typography['body2regular']}
    text-overflow: ellipsis;
    overflow: hidden;
  }

  @media (${device.tablet}) {
    & > .welcome {
      ${typography['headline2mobile']}
    }
  }
`;
